import React, { useEffect, useMemo, useState } from "react";
import { Form, Formik } from "formik";
import { useStore } from "../../../GlobalProvider/GlobalProvider";
import { baseUrl } from "../../../utils/helper";
const moment = require("moment");

const inputClass = "w-full border border-gray-200 rounded-lg px-2.5 py-2 text-sm focus:outline-none focus:border-[#12AB81] bg-white";
const labelClass = "text-xs font-semibold text-gray-500 mb-1 block";
const unitClass = "text-xs text-gray-400 flex-shrink-0";

const VitalSignForm = ({ previousVitals, handleVitalsSubmit, patient }) => {
    const [height, setHeight] = useState(0);
    const [heightMesure, setHeightMesure] = useState("1");
    const [info, setInfo] = useState({});
    const [previousVitalsList, setPreviousVitalsList] = useState([]);
    const { token } = useStore();

    const setInfoFun = (key, value) => setInfo((prev) => ({ ...prev, [key]: value }));

    useEffect(() => {
        if (patient?._id) getPreviouVitals(patient._id);
    }, [patient]);

    function getPayload(payload = {}) {
        payload["vitals.height"] = ((+info?.height_ft || 0) + "." + (+info?.height_inc || 0)) || "";
        payload["vitals.weight"] = (+info?.weight_kg || 0).toFixed(2).toString();
        payload["vitals.bmi"] = info?.bmi || "";
        payload["vitals.bp"] = (info?.bp_upper || "") + "/" + (info?.bp_lower || "");
        payload["vitals.hr"] = info?.hr || "";
        payload["vitals.temperature"] = info?.temperature || "";
        payload["vitals.spo2"] = info?.spo2 || "";
        payload["vitals.rr"] = info?.rr || "";
        payload["vitals.note"] = info?.note || "";
        payload["vitals.date"] = moment().format("YYYY-MM-DD");
        return payload;
    }

    const onSubmit = async (values) => handleVitalsSubmit(getPayload(values));

    const getPreviouVitals = async (id) => {
        try {
            const response = await fetch(`${baseUrl}/patient/vitals/${id}`, {
                method: "GET",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            });
            const res = await response.json();
            if (res?.length > 0) setPreviousVitalsList(res);
        } catch (error) { console.error("Error:", error); }
    };

    const applyPreviousVitals = (prev) => {
        const bp = prev.vitals?.bp?.split("/");
        const ht = prev.vitals?.height?.split(".");
        if (prev.vitals?.height) {
            const cm = Math.floor(prev.vitals.height * 12 * 2.54);
            setHeight(cm);
        }
        setInfo({
            weight_kg: parseFloat(prev.vitals?.weight) || "",
            height_ft: ht?.length > 0 ? ht[0] : "",
            height_inc: ht?.length > 1 ? ht[1] : "",
            bp_upper: bp?.length > 0 ? bp[0] : "",
            bp_lower: bp?.length > 1 ? bp[1] : "",
            hr: prev.vitals?.hr || "",
            bmi: prev.vitals?.bmi || "",
            temperature: prev.vitals?.temperature || "",
            spo2: prev.vitals?.spo2 || "",
            rr: prev.vitals?.rr || "",
            note: prev.vitals?.note || "",
        });
    };

    useMemo(() => {
        if (previousVitals) {
            const bp = previousVitals?.bp?.split("/");
            const ht = previousVitals?.height?.split(".");
            if (previousVitals?.height) setHeight(Math.floor(previousVitals.height * 12 * 2.54));
            setInfo({
                weight_kg: parseFloat(previousVitals?.weight) || "",
                height_ft: ht?.length > 0 ? ht[0] : "",
                height_inc: ht?.length > 1 ? ht[1] : "",
                bp_upper: bp?.length > 0 ? bp[0] : "",
                bp_lower: bp?.length > 1 ? bp[1] : "",
                hr: previousVitals?.hr || "",
                bmi: previousVitals?.bmi || "",
                temperature: previousVitals?.temperature || "",
                spo2: previousVitals?.spo2 || "",
                rr: previousVitals?.rr || "",
                note: previousVitals?.note || "",
            });
        }
    }, [previousVitals]);

    useMemo(() => {
        if ((info.height_ft || info.height_inc) && info.weight_kg) {
            const h = (+info?.height_ft || 0) * 0.3048 + (+info?.height_inc || 0) * 0.0254;
            const w = +info?.weight_kg || 0;
            setInfoFun("bmi", h > 0 ? (w / Math.pow(h, 2)).toFixed(2) : "");
        } else {
            setInfoFun("bmi", "");
        }
    }, [info.height_ft, info.height_inc, info.weight_kg]);

    useMemo(() => {
        if (height && heightMesure == 2) {
            const totalInches = height / 2.54;
            setInfo((prev) => ({ ...prev, height_ft: parseInt(totalInches / 12), height_inc: Math.floor(totalInches % 12) }));
        }
    }, [height, heightMesure]);

    return (
        <div className="px-1">
            {previousVitalsList?.length > 0 && (
                <button
                    type="button"
                    onClick={() => applyPreviousVitals(previousVitalsList[0] || {})}
                    className="w-full mb-4 py-2 rounded-xl border border-[#12AB81] text-[#12AB81] text-sm font-semibold active:bg-green-50"
                >
                    Use previous vitals
                </button>
            )}

            <Formik initialValues={{}} onSubmit={onSubmit}>
                {() => (
                    <Form className="space-y-4">

                        {/* Height */}
                        <div>
                            <div className="flex items-center justify-between mb-1.5">
                                <label className={labelClass} style={{ marginBottom: 0 }}>Height</label>
                                <div className="flex items-center gap-3 text-sm">
                                    {[{ val: "1", label: "ft / in" }, { val: "2", label: "cm" }].map((opt) => (
                                        <label key={opt.val} className="flex items-center gap-1.5 cursor-pointer text-xs text-gray-500">
                                            <input
                                                type="radio"
                                                name="heightUnit"
                                                value={opt.val}
                                                checked={heightMesure === opt.val}
                                                onChange={() => setHeightMesure(opt.val)}
                                                className="accent-[#12AB81]"
                                            />
                                            {opt.label}
                                        </label>
                                    ))}
                                </div>
                            </div>
                            {heightMesure === "1" ? (
                                <div className="flex items-center gap-2">
                                    <input className={`${inputClass} flex-1`} type="number" placeholder="ft" value={info?.height_ft || ""} onChange={(e) => setInfoFun("height_ft", e.target.value)} />
                                    <span className={unitClass}>ft</span>
                                    <input className={`${inputClass} flex-1`} type="number" placeholder="in" value={info?.height_inc || ""} onChange={(e) => setInfoFun("height_inc", e.target.value)} />
                                    <span className={unitClass}>in</span>
                                </div>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <input className={`${inputClass} flex-1`} type="number" placeholder="cm" value={height || ""} onChange={(e) => setHeight(e.target.value)} />
                                    <span className={unitClass}>cm</span>
                                </div>
                            )}
                        </div>

                        {/* Weight */}
                        <div>
                            <label className={labelClass}>Weight</label>
                            <div className="flex items-center gap-2">
                                <input
                                    className={`${inputClass} flex-1`}
                                    type="number"
                                    step="0.1"
                                    placeholder="e.g. 65.5"
                                    value={info?.weight_kg || ""}
                                    onChange={(e) => setInfoFun("weight_kg", e.target.value)}
                                />
                                <span className={unitClass}>kg</span>
                            </div>
                        </div>

                        {/* BMI + Temperature */}
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className={labelClass}>BMI</label>
                                <div className="flex items-center gap-1.5">
                                    <input className={`${inputClass} flex-1`} type="text" placeholder="auto" value={info?.bmi || ""} onChange={(e) => setInfoFun("bmi", e.target.value)} />
                                </div>
                            </div>
                            <div>
                                <label className={labelClass}>Temperature</label>
                                <div className="flex items-center gap-1.5">
                                    <input className={`${inputClass} flex-1`} type="number" placeholder="°F" value={info?.temperature || ""} onChange={(e) => setInfoFun("temperature", e.target.value)} />
                                    <span className={unitClass}>°F</span>
                                </div>
                            </div>
                        </div>

                        {/* Blood Pressure */}
                        <div>
                            <label className={labelClass}>Blood Pressure</label>
                            <div className="flex items-center gap-2">
                                <input className={`${inputClass} flex-1`} type="number" placeholder="Systolic" value={info?.bp_upper || ""} onChange={(e) => setInfoFun("bp_upper", e.target.value)} />
                                <span className="text-gray-400 font-medium">/</span>
                                <input className={`${inputClass} flex-1`} type="number" placeholder="Diastolic" value={info?.bp_lower || ""} onChange={(e) => setInfoFun("bp_lower", e.target.value)} />
                                <span className={unitClass}>mmHg</span>
                            </div>
                        </div>

                        {/* Heart Rate + SPO2 */}
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className={labelClass}>Heart Rate</label>
                                <div className="flex items-center gap-1.5">
                                    <input className={`${inputClass} flex-1`} type="number" placeholder="bpm" value={info?.hr || ""} onChange={(e) => setInfoFun("hr", e.target.value)} />
                                    <span className={unitClass}>/min</span>
                                </div>
                            </div>
                            <div>
                                <label className={labelClass}>SPO2</label>
                                <div className="flex items-center gap-1.5">
                                    <input className={`${inputClass} flex-1`} type="number" placeholder="%" value={info?.spo2 || ""} onChange={(e) => setInfoFun("spo2", e.target.value)} />
                                    <span className={unitClass}>%</span>
                                </div>
                            </div>
                        </div>

                        {/* Respiratory Rate */}
                        <div>
                            <label className={labelClass}>Respiratory Rate</label>
                            <div className="flex items-center gap-2">
                                <input className={`${inputClass} flex-1`} type="number" placeholder="breaths/min" value={info?.rr || ""} onChange={(e) => setInfoFun("rr", e.target.value)} />
                                <span className={unitClass}>/min</span>
                            </div>
                        </div>

                        {/* Note */}
                        <div>
                            <label className={labelClass}>Additional Note</label>
                            <textarea
                                className={`${inputClass} resize-none`}
                                rows={3}
                                placeholder="Any additional notes..."
                                value={info?.note || ""}
                                onChange={(e) => setInfoFun("note", e.target.value)}
                            />
                        </div>

                        <button
                            type="submit"
                            className="w-full py-3 bg-[#12AB81] text-white rounded-xl font-semibold text-sm active:opacity-80"
                        >
                            Save Vitals
                        </button>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default VitalSignForm;
